import React from "react";
import { formatMessage } from "lib/globalize-util";
import { mergeStyleSets, nuOrderTheme as theme, ConfirmDialog, Dropdown, IDropdownOption } from "@nuorder/components";
import { CollectionType, ICreateAssortmentRequest } from "containers/folders/types/assortment";
import { FOLDERS } from "constants/integration/folders";
import { WindowAnalytics } from "helpers/window-analytics";
import { AssortmentActions } from "constants/analytics";
import { IBrand } from "containers/folders/types/brands";
import { useFetchAssortmentMerchants } from "containers/management/merchant-inventory-settings/hooks/merchant";
import { useFlags } from "launchdarkly-react-client-sdk";

interface IOwnProps {
    onClose: () => void;
    onConfirm: (params: ICreateAssortmentRequest) => void;
    merchantId: string;
    parentId: string;
    isLoading?: boolean;
    errorMessage?: string;
    brand?: IBrand;
}

const classNames = mergeStyleSets({
    messageError: {
        fontSize: 14,
        color: theme.palette.redDark,
        paddingTop: 10,
        wordBreak: "break-word",
    },
});

export const CreateBrandAssortmentDialog: React.FunctionComponent<IOwnProps> = ({
    onConfirm,
    isLoading,
    onClose,
    merchantId,
    errorMessage,
    parentId,
    brand,
}) => {
    const [selectedMerchant, setSelectedMerchant] = React.useState<string>("");
    const { data: merchants } = useFetchAssortmentMerchants();

    const flag = useFlags();
    const handleCreateAssortment = React.useCallback(() => {
        onConfirm({
            merchantId,
            targetMerchantId: selectedMerchant,
            parentId: parentId,
            name: formatMessage("Untitled Assortment"),
            inventoryEnabled: true,
            brandId: brand._id,
            type: CollectionType.BrandStandard,
            allocationBagVersion1: flag.allocationBagVersion1,
        });

        WindowAnalytics.track(AssortmentActions.CreateAssortment, {
            assortmentType: CollectionType.BrandStandard,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedMerchant, parentId]);

    const merchantOptions: IDropdownOption[] = React.useMemo(
        () =>
            merchants?.map((merchant) => ({
                id: merchant.value,
                key: merchant.value,
                text: merchant.label,
            })) || [],
        [merchants]
    );

    const handleChangeMerchant = React.useCallback(
        (_value, option: IDropdownOption) => {
            setSelectedMerchant(option.key as string);
        },
        [setSelectedMerchant]
    );

    return (
        <ConfirmDialog
            contentProps={{ title: formatMessage("New Assortment") }}
            onConfirm={handleCreateAssortment}
            onClose={onClose}
            open={true}
            isLoading={isLoading}
            confirmButtonDisabled={isLoading || !selectedMerchant}
            data-test={FOLDERS.CREATE_ASSORTMENT_DIALOG.CONTAINER}
            okLabel={formatMessage("Create")}
            cancelLabel={formatMessage("Cancel")}
            closeButtonAriaLabel={formatMessage("Close")}
        >
            <Dropdown
                testId={FOLDERS.CREATE_ASSORTMENT_DIALOG.ASSORTMENT_MERCHANT_DROPDOWN}
                selectedKey={selectedMerchant}
                label={formatMessage("Retailer")}
                options={merchantOptions}
                onChange={handleChangeMerchant}
                isSearchable
                placeholder={formatMessage("Select Retailer")}
            />
            <p data-test={FOLDERS.CREATE_ASSORTMENT_DIALOG.MESSAGE_ERROR} className={classNames.messageError}>
                {errorMessage}
            </p>
        </ConfirmDialog>
    );
};
